import { useState } from 'react';
import { Check, Copy, FileText, Paperclip, Smartphone, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import { requisitarFaturasMes } from '@/hooks/useFaturasMes';
import { AnexarConta } from '@/components/kanban/fluxo/TriagemPrimeiraFatura';
import { formatarDocumento } from '@/components/kanban/triagem/segmentoTriagem';

/* A TRAVA DA CONTA RESOLVE ALI MESMO (Douglas 23/10/2026, maquete aprovada):
   cada pedido diz qual conta falta e o CNPJ para colar no SOLAR, e tem Pagou,
   Anexar e Justificar na própria linha. Os botões são os da Triagem Faturas,
   apontados para a conta que a trava cobra (fn_qualidade_trava_conta). */

export type ContaDaTrava = {
  id: string; tipo: 'movel' | 'fixa'; numero: number; vencimento: string | null;
  pagamento: 'pago' | 'nao_pago' | null; tem_conta: boolean; feito: 'pagou' | 'anexou' | null;
  documento: string | null; razao_social: string | null;
};
export type PedidoContaTrava = {
  pedido_id: string; numero_pedido: number | null; cliente: string | null; batido: boolean;
  conta?: ContaDaTrava | null; justificativa?: string | null;
};

const BTN = 'inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-border px-1.5 text-[11px] font-semibold text-foreground hover:border-primary/50 hover:text-primary disabled:opacity-40';
const VERDE = 'text-[#075E54] dark:text-[#25D366]';

const mesDa = (d: string | null | undefined) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR', { month: 'long' }) : '');
const diaMes = (d: string | null | undefined) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : '-');

function descricao(c: ContaDaTrava | null | undefined) {
  if (!c) return 'conta vencida sem conta';
  return `${c.numero}ª conta · ${mesDa(c.vencimento)} · venceu ${diaMes(c.vencimento)}`;
}

function Linha({ p, aoMudar, aoJustificar }: { p: PedidoContaTrava; aoMudar: () => void; aoJustificar: () => void }) {
  const c = p.conta;
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const copiar = () => {
    if (!c?.documento) return;
    void navigator.clipboard.writeText(c.documento.replace(/\D/g, '')).then(() => toast.success('CNPJ copiado para colar no SOLAR.'));
  };
  const pagou = async () => {
    if (!c) return;
    setOcupado(true);
    try {
      await requisitarFaturasMes(`${c.id}/pagamento`, { situacao: 'pago', sem_conta: true });
      toast.success(`${c.numero}ª conta de ${mesDa(c.vencimento)} marcada como paga.`);
      aoMudar();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Não foi possível marcar como paga.'); }
    finally { setOcupado(false); setConfirmando(false); }
  };
  const ligar = async (faturaId: string) => {
    if (!c) return;
    setOcupado(true);
    try {
      const r = await requisitarFaturasMes<{ ligada: boolean; motivo?: string; whatsapp?: boolean; email?: boolean }>(`${c.id}/anexada`, { fatura_id: faturaId });
      if (!r.ligada) toast.info(r.motivo || 'A conta foi guardada.');
      else {
        const canais = [r.whatsapp ? 'WhatsApp' : null, r.email ? 'e-mail' : null].filter(Boolean).join(' e ');
        toast.success(`Conta anexada${canais ? ` e enviada por ${canais}` : ''}.`);
      }
    } catch (e) {
      toast.error('A conta foi lida, mas não entrou no pedido', { description: e instanceof Error ? e.message : undefined, duration: 12000 });
    } finally { setOcupado(false); aoMudar(); }
  };

  const feito = p.batido && !p.justificativa;
  const sub = p.batido
    ? p.justificativa ? `${p.justificativa} · volta na próxima trava`
      : c?.feito === 'pagou' ? `${descricao(c)} · paga, conferida no SOLAR`
      : c?.feito === 'anexou' ? `${descricao(c)} · conta anexada e enviada`
      : descricao(c)
    : null;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 border-t border-border py-1.5">
      <div className="min-w-0">
        <p className="truncate text-[12.5px]" title={p.cliente || undefined}>
          <span className="font-semibold tabular-nums text-foreground">{p.numero_pedido ?? '-'}</span>
          <span className="text-muted-foreground"> · {p.cliente || '-'}</span>
        </p>
        {sub
          ? <p className={`text-[11px] leading-snug ${feito ? VERDE : 'text-muted-foreground'}`}>{sub}</p>
          : <p className="text-[11px] leading-snug text-muted-foreground">
              {c?.documento && <>
                <button type="button" onClick={copiar} title="Copiar o CNPJ para colar no SOLAR"
                  className="inline-flex items-center gap-1 tabular-nums hover:text-primary">
                  {formatarDocumento(c.documento)}<Copy className="h-3 w-3 text-primary" aria-hidden="true" />
                </button>
                <span aria-hidden="true"> · </span>
              </>}
              {descricao(c)}
            </p>}
      </div>
      <div className="flex items-center gap-1.5">
        {!p.batido && c && (confirmando
          ? <>
              <button type="button" onClick={() => setConfirmando(false)} disabled={ocupado} className={BTN}>Cancelar</button>
              <button type="button" onClick={() => void pagou()} disabled={ocupado} className={`${BTN} ${VERDE}`}>
                <Check className="h-3 w-3" aria-hidden="true" />{ocupado ? 'Marcando…' : `Confirmar pago · ${mesDa(c.vencimento)}`}
              </button>
            </>
          : <>
              <button type="button" onClick={() => setConfirmando(true)} disabled={ocupado} title="Conferi no SOLAR: o cliente pagou esta conta"
                className={BTN}><Check className="h-3 w-3" aria-hidden="true" />Pagou</button>
              <AnexarConta l={{ documento: c.documento || '', razao_social: c.razao_social || p.cliente || '', tipo: c.tipo }} aoTerminar={id => void ligar(id)}>
                {(abrir, lendo) => (
                  <button type="button" onClick={abrir} disabled={ocupado || lendo} title="Baixe a conta no SOLAR e anexe o PDF" className={BTN}>
                    <Paperclip className="h-3 w-3" aria-hidden="true" />{lendo || ocupado ? 'Lendo…' : 'Anexar'}
                  </button>
                )}
              </AnexarConta>
            </>)}
        {!p.batido && !confirmando && (
          <button type="button" onClick={aoJustificar} className={BTN}><FileText className="h-3 w-3" aria-hidden="true" />Justificar</button>
        )}
        <span className={`w-[64px] text-right text-[11px] ${feito ? `font-semibold ${VERDE}` : p.batido ? 'text-muted-foreground' : 'text-primary'}`}>
          {p.batido ? (p.justificativa ? 'justificado' : 'feito') : 'falta'}
        </span>
      </div>
    </div>
  );
}

export function TravaContaVencidaLista({ pedidos, aoMudar, aoJustificar }: {
  pedidos: PedidoContaTrava[]; aoMudar: () => void; aoJustificar: (p: PedidoContaTrava) => void;
}) {
  const grupos = ([
    { tipo: 'fixa', titulo: 'Fixa · internet', dica: 'baixar no SOLAR', Icone: Wifi },
    { tipo: 'movel', titulo: 'Móvel · celular', dica: 'Bee ou SOLAR', Icone: Smartphone },
  ] as const).map(g => ({ ...g, linhas: pedidos.filter(p => (p.conta?.tipo ?? 'movel') === g.tipo) })).filter(g => g.linhas.length > 0);
  return <>
    {grupos.map(g => (
      <div key={g.tipo} className="mb-2">
        <p className="mt-2 flex items-center gap-1.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <g.Icone className="h-3.5 w-3.5 text-primary" aria-hidden="true" />{g.titulo}
          <span className="ml-auto font-normal normal-case tracking-normal">{g.dica} · faltam {g.linhas.filter(p => !p.batido).length}</span>
        </p>
        {g.linhas.map((p, i) => <Linha key={`${p.pedido_id}-${i}`} p={p} aoMudar={aoMudar} aoJustificar={() => aoJustificar(p)} />)}
      </div>
    ))}
  </>;
}
